import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import type { Category } from "../../api/types";

const FUNDING_LABEL: Record<string, string> = {
  employee: "Employee (reimbursement)",
  petty_cash: "Petty cash",
  activity_budget: "Activity budget",
};

const blank: Partial<Category> = {
  code: "",
  name: "",
  funding_source: "employee",
  is_mileage: false,
  description_required: false,
  active: true,
  sort_order: 100,
};

type BulkMode = null | "receipt" | "cap" | "funding";

export default function CategoriesAdmin() {
  const qc = useQueryClient();
  const { data, isLoading, error: loadError } = useQuery({ queryKey: ["admin-categories"], queryFn: api.admin.listCategories });
  const [editing, setEditing] = useState<Partial<Category> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkMode, setBulkMode] = useState<BulkMode>(null);
  const [bulkValue, setBulkValue] = useState<string>("");
  const [bulkResult, setBulkResult] = useState<{ ok: number; failed: { id: number; reason: string }[] } | null>(null);

  const save = useMutation({
    mutationFn: (c: Partial<Category>) =>
      c.id ? api.admin.updateCategory(c.id, c) : api.admin.createCategory(c),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-categories"] });
      qc.invalidateQueries({ queryKey: ["categories"] });
      setEditing(null);
      setError(null);
    },
    onError: (e) => setError((e as Error).message),
  });

  const deactivate = useMutation({
    mutationFn: (id: number) => api.admin.deactivateCategory(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-categories"] }),
  });

  const allIds = useMemo(() => (data ?? []).map((c) => c.id), [data]);
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));

  function toggle(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }
  function toggleAll() {
    setSelected((prev) => {
      if (allIds.every((id) => prev.has(id))) {
        const next = new Set(prev);
        for (const id of allIds) next.delete(id);
        return next;
      }
      return new Set(allIds);
    });
  }

  /**
   * Apply a partial patch to every selected row, looping the existing PUT endpoint.
   * Each call merges the patch into the original so required fields stay populated.
   */
  const bulkApply = useMutation({
    mutationFn: async (patch: Partial<Category>) => {
      const ids = [...selected];
      const ok: number[] = [];
      const failed: { id: number; reason: string }[] = [];
      for (const id of ids) {
        const original = (data ?? []).find((c) => c.id === id);
        if (!original) { failed.push({ id, reason: "Not loaded" }); continue; }
        try {
          await api.admin.updateCategory(id, { ...original, ...patch });
          ok.push(id);
        } catch (err) {
          failed.push({ id, reason: (err as Error).message });
        }
      }
      return { ok, failed };
    },
    onSuccess: (res) => {
      setBulkResult({ ok: res.ok.length, failed: res.failed });
      setBulkMode(null);
      setBulkValue("");
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ["admin-categories"] });
      qc.invalidateQueries({ queryKey: ["categories"] });
    },
  });

  const bulkDeactivate = useMutation({
    mutationFn: async () => {
      const ids = [...selected];
      const ok: number[] = [];
      const failed: { id: number; reason: string }[] = [];
      for (const id of ids) {
        try {
          await api.admin.deactivateCategory(id);
          ok.push(id);
        } catch (err) {
          failed.push({ id, reason: (err as Error).message });
        }
      }
      return { ok, failed };
    },
    onSuccess: (res) => {
      setBulkResult({ ok: res.ok.length, failed: res.failed });
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ["admin-categories"] });
    },
  });

  function commitBulk() {
    if (bulkMode === "receipt") {
      const v = bulkValue.trim() === "" ? null : Number(bulkValue);
      bulkApply.mutate({ requires_receipt_above: v });
    } else if (bulkMode === "cap") {
      const v = bulkValue.trim() === "" ? null : Number(bulkValue);
      bulkApply.mutate({ cap_amount: v });
    } else if (bulkMode === "funding") {
      bulkApply.mutate({ funding_source: bulkValue as Category["funding_source"] });
    }
  }

  const busy = bulkApply.isPending || bulkDeactivate.isPending;

  return (
    <div className="space-y-6">
      <Link to="/admin" className="text-sm text-slate-500 hover:text-slate-700">← Back to admin</Link>

      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-liaise-navy">Categories</h1>
          <p className="text-slate-600 mt-1">Categories shown to claimants when adding expense lines.</p>
        </div>
        <button
          onClick={() => { setEditing({ ...blank }); setError(null); }}
          className="bg-liaise-navy text-white font-medium px-5 py-2.5 rounded-lg hover:bg-liaise-navy-700"
        >
          + New category
        </button>
      </div>

      {selected.size > 0 && bulkMode === null && (
        <div className="bg-liaise-navy text-white rounded-xl px-5 py-3 flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm"><span className="font-medium">{selected.size}</span> selected</div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setSelected(new Set())} className="text-white/80 text-sm hover:text-white">Clear</button>
            <button
              onClick={() => bulkApply.mutate({ active: true })}
              disabled={busy}
              className="bg-emerald-500 text-white text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-emerald-600 disabled:opacity-50"
            >
              Activate
            </button>
            <button
              onClick={() => { if (confirm(`Deactivate ${selected.size} categor${selected.size === 1 ? "y" : "ies"}?`)) bulkDeactivate.mutate(); }}
              disabled={busy}
              className="bg-red-500 text-white text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-red-600 disabled:opacity-50"
            >
              Deactivate
            </button>
            <button onClick={() => { setBulkMode("receipt"); setBulkValue(""); }} disabled={busy}
              className="bg-white text-liaise-navy text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-50">
              Set receipt threshold…
            </button>
            <button onClick={() => { setBulkMode("cap"); setBulkValue(""); }} disabled={busy}
              className="bg-white text-liaise-navy text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-50">
              Set soft cap…
            </button>
            <button onClick={() => { setBulkMode("funding"); setBulkValue("employee"); }} disabled={busy}
              className="bg-white text-liaise-navy text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-50">
              Set funding…
            </button>
          </div>
        </div>
      )}

      {bulkMode && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl px-5 py-4 space-y-3">
          <div className="flex items-baseline justify-between">
            <h3 className="font-medium text-amber-900">
              {bulkMode === "receipt" && `Set receipt-above for ${selected.size} categor${selected.size === 1 ? "y" : "ies"}`}
              {bulkMode === "cap" && `Set soft cap for ${selected.size} categor${selected.size === 1 ? "y" : "ies"}`}
              {bulkMode === "funding" && `Set funding source for ${selected.size} categor${selected.size === 1 ? "y" : "ies"}`}
            </h3>
            <button onClick={() => setBulkMode(null)} className="text-xs text-amber-700 hover:underline">Cancel</button>
          </div>

          {bulkMode === "receipt" && (
            <label className="text-sm block">
              <span className="block font-medium text-slate-700 mb-1">Receipt required above (£)</span>
              <input type="number" step="0.01" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} placeholder="Leave blank to clear" className="w-full max-w-xs border border-amber-300 rounded-lg px-3 py-2 bg-white" />
            </label>
          )}
          {bulkMode === "cap" && (
            <label className="text-sm block">
              <span className="block font-medium text-slate-700 mb-1">Soft cap (£)</span>
              <input type="number" step="0.01" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} placeholder="Leave blank to clear" className="w-full max-w-xs border border-amber-300 rounded-lg px-3 py-2 bg-white" />
            </label>
          )}
          {bulkMode === "funding" && (
            <label className="text-sm block">
              <span className="block font-medium text-slate-700 mb-1">Funding source</span>
              <select value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} className="w-full max-w-xs border border-amber-300 rounded-lg px-3 py-2 bg-white">
                <option value="employee">Employee (reimbursement)</option>
                <option value="petty_cash">Petty cash</option>
                <option value="activity_budget">Activity budget</option>
              </select>
            </label>
          )}

          <div className="flex gap-2">
            <button onClick={commitBulk} disabled={busy} className="bg-amber-600 text-white font-medium px-4 py-2 rounded-lg hover:bg-amber-700 disabled:opacity-50">
              {busy ? "Applying…" : `Apply to ${selected.size}`}
            </button>
          </div>
        </div>
      )}

      {bulkResult && (
        <div className={`rounded-xl px-5 py-3 text-sm border ${bulkResult.failed.length === 0 ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-900"}`}>
          <div><strong>{bulkResult.ok}</strong> updated{bulkResult.failed.length > 0 && <>, <strong>{bulkResult.failed.length}</strong> failed</>}.</div>
          {bulkResult.failed.length > 0 && (
            <ul className="mt-2 space-y-1 text-xs">
              {bulkResult.failed.map((f, i) => <li key={i}>· id {f.id} — {f.reason}</li>)}
            </ul>
          )}
          <button onClick={() => setBulkResult(null)} className="text-xs underline mt-2">Dismiss</button>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        {isLoading && <div className="px-6 py-12 text-center text-slate-500">Loading…</div>}
        {loadError && (
          <div className="px-6 py-8 text-red-700 bg-red-50 border-b border-red-200 text-sm">
            <div className="font-medium">Failed to load categories</div>
            <div className="mt-1 font-mono text-xs">{(loadError as Error).message}</div>
          </div>
        )}
        {data && data.length === 0 && !isLoading && (
          <div className="px-6 py-12 text-center text-slate-500">No categories yet — click "+ New category" to add one.</div>
        )}
        {data && data.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left">
              <tr>
                <th className="px-4 py-3 w-10"><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>
                <th className="px-6 py-3 font-medium">Code</th>
                <th className="px-6 py-3 font-medium">Name</th>
                <th className="px-6 py-3 font-medium">Funding</th>
                <th className="px-6 py-3 font-medium">Receipt above</th>
                <th className="px-6 py-3 font-medium">Cap</th>
                <th className="px-6 py-3 font-medium">Active</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.map((c) => (
                <tr key={c.id} className={!c.active ? "bg-slate-50/60 text-slate-400" : ""}>
                  <td className="px-4 py-3"><input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} /></td>
                  <td className="px-6 py-3 font-mono text-xs">{c.code}</td>
                  <td className="px-6 py-3">{c.name}{c.is_mileage && <span className="ml-2 text-xs bg-liaise-navy/10 text-liaise-navy px-1.5 py-0.5 rounded">mileage</span>}</td>
                  <td className="px-6 py-3 text-slate-600">{FUNDING_LABEL[c.funding_source] ?? c.funding_source}</td>
                  <td className="px-6 py-3 text-slate-600">{c.requires_receipt_above != null ? `£${c.requires_receipt_above}` : "—"}</td>
                  <td className="px-6 py-3 text-slate-600">{c.cap_amount != null ? `£${c.cap_amount}` : "—"}</td>
                  <td className="px-6 py-3">{c.active ? "✓" : "—"}</td>
                  <td className="px-6 py-3 text-right space-x-3 whitespace-nowrap">
                    <button onClick={() => { setEditing({ ...c }); setError(null); }} className="text-liaise-navy text-xs hover:underline">Edit</button>
                    {c.active && (
                      <button onClick={() => deactivate.mutate(c.id)} className="text-red-600 text-xs hover:underline">Deactivate</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-semibold text-liaise-navy">
              {editing.id ? "Edit category" : "New category"}
            </h2>

            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm">
                <span className="block font-medium text-slate-700 mb-1">Code</span>
                <input
                  value={editing.code ?? ""}
                  onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 font-mono text-sm"
                  placeholder="ACC"
                />
              </label>
              <label className="text-sm">
                <span className="block font-medium text-slate-700 mb-1">Name</span>
                <input
                  value={editing.name ?? ""}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2"
                  placeholder="Accommodation"
                />
              </label>
            </div>

            <label className="text-sm block">
              <span className="block font-medium text-slate-700 mb-1">Funding source</span>
              <select
                value={editing.funding_source ?? "employee"}
                onChange={(e) => setEditing({ ...editing, funding_source: e.target.value as Category["funding_source"] })}
                className="w-full border border-slate-300 rounded-lg px-3 py-2"
              >
                <option value="employee">Employee (reimbursement)</option>
                <option value="petty_cash">Petty cash</option>
                <option value="activity_budget">Activity budget</option>
              </select>
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm">
                <span className="block font-medium text-slate-700 mb-1">Receipt required above (£)</span>
                <input
                  type="number"
                  step="0.01"
                  value={editing.requires_receipt_above ?? ""}
                  onChange={(e) => setEditing({ ...editing, requires_receipt_above: e.target.value === "" ? null : Number(e.target.value) })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2"
                />
              </label>
              <label className="text-sm">
                <span className="block font-medium text-slate-700 mb-1">Soft cap (£)</span>
                <input
                  type="number"
                  step="0.01"
                  value={editing.cap_amount ?? ""}
                  onChange={(e) => setEditing({ ...editing, cap_amount: e.target.value === "" ? null : Number(e.target.value) })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2"
                />
              </label>
            </div>

            <label className="text-sm block">
              <span className="block font-medium text-slate-700 mb-1">Cap message (optional)</span>
              <input
                value={editing.cap_message ?? ""}
                onChange={(e) => setEditing({ ...editing, cap_message: e.target.value === "" ? null : e.target.value })}
                className="w-full border border-slate-300 rounded-lg px-3 py-2"
                placeholder="Maximum £5.00 for this category."
                maxLength={500}
              />
              <span className="block text-xs text-slate-400 mt-1">Shown to claimants under the amount. Leave blank to auto-generate from the soft cap.</span>
            </label>

            <div className="grid grid-cols-3 gap-3">
              <label className="text-sm flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editing.is_mileage ?? false}
                  onChange={(e) => setEditing({ ...editing, is_mileage: e.target.checked })}
                />
                <span>Mileage</span>
              </label>
              <label className="text-sm flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editing.description_required ?? false}
                  onChange={(e) => setEditing({ ...editing, description_required: e.target.checked })}
                />
                <span>Description required</span>
              </label>
              <label className="text-sm flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editing.active ?? true}
                  onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                />
                <span>Active</span>
              </label>
            </div>

            <label className="text-sm block">
              <span className="block font-medium text-slate-700 mb-1">Sort order</span>
              <input
                type="number"
                value={editing.sort_order ?? 100}
                onChange={(e) => setEditing({ ...editing, sort_order: Number(e.target.value) })}
                className="w-full border border-slate-300 rounded-lg px-3 py-2"
              />
            </label>

            {error && <div className="text-red-600 text-sm">{error}</div>}

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => save.mutate(editing)}
                disabled={!editing.code || !editing.name || save.isPending}
                className="bg-liaise-navy text-white font-medium px-5 py-2.5 rounded-lg hover:bg-liaise-navy-700 disabled:opacity-50"
              >
                {save.isPending ? "Saving…" : "Save"}
              </button>
              <button onClick={() => setEditing(null)} className="text-slate-600 px-4 py-2 hover:underline">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
